/**
 * Pure helpers for the package domain (identity, kind predicates, pricing,
 * formatting, booking policy and row construction).
 *
 * Kept Vue-free so the rules can be unit-tested and reused across the
 * package landing page, the catalog and (in the future) the dashboard.
 */

const PACKAGE_ROW_DEFINITIONS = [
  {
    key: 'termine',
    label: 'Veranstaltungen',
    teaser: 'Mehr Reichweite für Ihre Termine und Events',
  },
  {
    key: 'unterkunft',
    label: 'Unterkünfte',
    teaser: 'Mehr Sichtbarkeit und bessere Buchungschancen',
  },
  {
    key: 'entdecken',
    label: 'Entdecken',
    teaser: 'Aktivitäten und Highlights für neugierige Gäste sichtbar machen',
  },
  {
    key: 'angebote',
    label: 'Angebote',
    teaser: 'Angebote gezielt vor interessierte Gäste bringen',
  },
]

const SOURCE_KIND_LABELS = {
  free: 'free',
  paid: 'kostenpflichtig',
}

const getPackageRowSortIndex = (key) =>
  PACKAGE_ROW_DEFINITIONS.findIndex((definition) => definition.key === key)

const sortPackageRows = (rows) =>
  [...rows].sort((a, b) => {
    const aHasPackages = a.packages.length > 0
    const bHasPackages = b.packages.length > 0
    if (aHasPackages !== bHasPackages) return aHasPackages ? -1 : 1

    const aActive = a.activePackages.length > 0
    const bActive = b.activePackages.length > 0
    if (aActive !== bActive) return aActive ? -1 : 1

    return getPackageRowSortIndex(a.key) - getPackageRowSortIndex(b.key)
  })

const toNumber = (value) => {
  const n = Number(value)
  return Number.isNaN(n) ? 0 : n
}

const sourceKind = (pkg) => String(pkg?.source_kind ?? pkg?.package?.source_kind ?? '').toLowerCase()

export const isFreePackage = (pkg) => sourceKind(pkg) === 'free'
export const isNonFreePackage = (pkg) => {
  const kind = sourceKind(pkg)
  return Boolean(kind) && kind !== 'free'
}
export const isPaidPackage = (pkg) => sourceKind(pkg) === 'paid'
export const isIndividualPackage = (pkg) => sourceKind(pkg) === 'individual'

export const hasActiveIndividualPackage = (activePackages = []) =>
  activePackages.some(isIndividualPackage)

export const getPackageIdentityKey = (pkg) =>
  String(pkg?.package_id ?? pkg?.package?.id ?? pkg?.id ?? '')

export const getPackageAssignmentId = (pkg) => String(pkg?.id ?? '')

export const getSortQuantity = (pkg) =>
  toNumber(pkg?.effective_quantity ?? pkg?.included_quantity ?? pkg?.quantity)

export const getExtraQuantityPriceCents = (pkg) =>
  toNumber(pkg?.price_per_extra_quantity_cents ?? pkg?.price_per_additional_unit_cents)

export const formatCents = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-'
  }
  const numeric = Number(value)
  if (Number.isNaN(numeric)) {
    return value
  }
  return (numeric / 100).toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
  })
}

export const formatPriceLabel = (value) => {
  const label = formatCents(value)
  const numeric = Number(value)
  return !Number.isNaN(numeric) && numeric > 0 ? `${label} pro Monat` : label
}

export const formatEndDate = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('de-DE')
}

export const getSourceKindLabel = (kind) => SOURCE_KIND_LABELS[kind] || 'Aktiv'

export const getTileTitle = (pkg) => getSourceKindLabel(pkg?.source_kind)

export const getTileQuantity = (pkg) => {
  const quantity = getSortQuantity(pkg)
  if (!quantity) return 'Flexible Anzahl an Einträgen'
  return `${quantity} ${quantity === 1 ? 'Eintrag' : 'Einträge'}`
}

export const showExtraQuantityPrice = (pkg) =>
  !isFreePackage(pkg) && getExtraQuantityPriceCents(pkg) > 0

export const getExtraQuantityPriceLabel = (pkg) =>
  `${formatCents(getExtraQuantityPriceCents(pkg))} je zusätzlichem Eintrag`

export const getDurationText = (pkg) =>
  pkg?.ends_at ? `Buchbar bis ${new Date(pkg.ends_at).toLocaleDateString('de-DE')}` : ''

export const getPackageBadge = (pkg, index) => {
  if (index === 0) return 'Empfohlen'
  return pkg?.ends_at ? 'Zeitlich verfügbar' : 'Direkt verfügbar'
}

export const getPackageTotalPriceCents = (pkg) => {
  const basePrice = toNumber(pkg?.price_cents)
  const extraPrice = getExtraQuantityPriceCents(pkg)
  const extraQuantity = isIndividualPackage(pkg) ? toNumber(pkg?.extra_quantity) : 0
  return basePrice + Math.max(extraQuantity, 0) * Math.max(extraPrice, 0)
}

export const getActivePackagePriceLabel = (pkg) =>
  isIndividualPackage(pkg) ? 'inklusive' : formatCents(getPackageTotalPriceCents(pkg))

export const getNextBillingPeriodStartLabel = (pkg) => {
  if (isFreePackage(pkg)) return '-'
  if (pkg?.ends_at) return 'gekündigt'

  const createdAt =
    pkg?.created_at
    || pkg?.createdAt
    || pkg?.package_assignment?.created_at
    || pkg?.packageAssignment?.created_at
    || pkg?.packageAssignment?.createdAt
    || pkg?.starts_at
    || pkg?.startsAt
  if (!createdAt) return '-'

  const start = new Date(createdAt)
  if (Number.isNaN(start.getTime())) return '-'

  const now = new Date()
  let nextStart = new Date(start)
  nextStart.setMonth(nextStart.getMonth() + 1)
  while (nextStart <= now) {
    const candidate = new Date(nextStart)
    candidate.setMonth(candidate.getMonth() + 1)
    nextStart = candidate
  }
  return nextStart.toLocaleDateString('de-DE')
}

const getActivePaidPackage = (row) =>
  (row?.activePackages || []).find(isPaidPackage) || null

export const isActivePackage = (row, pkg) =>
  (row?.activePackageKeys || []).includes(getPackageIdentityKey(pkg))

export const isUpgradeOption = (row, pkg) => {
  if (isFreePackage(pkg)) return false
  const active = getActivePaidPackage(row)
  if (!active || active.ends_at) return false
  return toNumber(pkg?.price_cents) > toNumber(active?.price_cents)
}

export const mustReactivateCurrentPackage = (row, pkg) => {
  if (isFreePackage(pkg)) return false
  const active = getActivePaidPackage(row)
  return Boolean(active?.ends_at)
}

export const mustBlockDowngrade = (row, pkg) => {
  if (isFreePackage(pkg)) return false
  const active = getActivePaidPackage(row)
  if (!active || active.ends_at) return false
  return toNumber(pkg?.price_cents) < toNumber(active?.price_cents)
}

/**
 * Free-package activation rules across all base types.
 *  - blocked when any paid package is active anywhere
 *  - allowed when at least one free package is active
 *  - otherwise allowed only when nothing is active
 */
export const mustBlockFreeActivation = (pkg, activePackages = []) => {
  if (!isFreePackage(pkg)) return false
  if (activePackages.some(isPaidPackage)) return true
  if (activePackages.some(isFreePackage)) return false
  return activePackages.length > 0
}

export const canShowPackageTile = (pkg, activePackages = []) =>
  !(isFreePackage(pkg) && mustBlockFreeActivation(pkg, activePackages))

export const getBookingCtaLabel = (row, pkg) => {
  if (isFreePackage(pkg)) return 'Jetzt aktivieren'
  if (isUpgradeOption(row, pkg)) return 'Paket hochstufen'
  return 'Paket buchen'
}

/**
 * Single CTA state for a catalog tile so the template stops branching.
 *
 * `kind`:
 *  - 'active'      : already booked, render "Paket buchen" disabled with info popover
 *  - 'enabled'     : router-link to booking route
 *  - 'downgrade'   : disabled with downgrade info popover
 *  - 'reactivate'  : disabled, prompts user to reactivate current package first
 */
export const getTileCtaState = (row, pkg, routeQuery = {}) => {
  if (isActivePackage(row, pkg)) {
    return {kind: 'active', label: 'Paket buchen'}
  }
  if (mustBlockDowngrade(row, pkg)) {
    return {kind: 'downgrade', label: 'Paket buchen'}
  }
  if (mustReactivateCurrentPackage(row, pkg)) {
    return {kind: 'reactivate', label: 'Zuerst aktuelles Paket reaktivieren'}
  }

  const label = getBookingCtaLabel(row, pkg)

  if (isUpgradeOption(row, pkg)) {
    const active = getActivePaidPackage(row)
    return {
      kind: 'enabled',
      label,
      route: {
        name: 'packages.book',
        params: {id: getPackageAssignmentId(active)},
        query: {...routeQuery, upgrade_package_id: pkg.id},
      },
    }
  }

  return {
    kind: 'enabled',
    label,
    route: {
      name: 'packages.book',
      params: {id: pkg.id},
      query: {...routeQuery},
    },
  }
}

/**
 * Build the row model used by both the status overview and the catalog.
 * Rows with bookable tiles float to the top; among those, active packages
 * rank higher. Categories without available tiles sink to the bottom even
 * when a subscription is already active. Free tiles are filtered by the
 * cross-row free-activation rules.
 */
export const buildPackageRows = (activePackages = [], featuredPackages = []) =>
  sortPackageRows(PACKAGE_ROW_DEFINITIONS.map((row) => {
      const rowActive = activePackages.filter((pkg) => pkg?.base_type === row.key)
      const activePackageKeys = rowActive.map(getPackageIdentityKey)
      const merged = [...rowActive]

      featuredPackages
        .filter((pkg) => pkg?.base_type === row.key)
        .forEach((pkg) => {
          if (!merged.some((existing) => getPackageIdentityKey(existing) === getPackageIdentityKey(pkg))) {
            merged.push(pkg)
          }
        })

      const sorted = merged
        .sort((a, b) => {
          const aFree = isFreePackage(a)
          const bFree = isFreePackage(b)
          if (aFree !== bFree) return aFree ? -1 : 1

          const aActive = activePackageKeys.includes(getPackageIdentityKey(a))
          const bActive = activePackageKeys.includes(getPackageIdentityKey(b))
          if (aActive !== bActive) return aActive ? -1 : 1

          return getSortQuantity(a) - getSortQuantity(b)
        })
        .filter((pkg) => canShowPackageTile(pkg, activePackages))

      return {
        ...row,
        activePackages: rowActive,
        activePackageKeys,
        packages: sorted,
      }
    }))

export const splitStatusRows = (rows, activePackages) => {
  const rowsByKey = Object.fromEntries(rows.map((row) => [row.key, row]))
  const allRows = sortPackageRows(PACKAGE_ROW_DEFINITIONS.map((definition) => rowsByKey[definition.key] || {
    ...definition,
    activePackages: [],
    activePackageKeys: [],
    packages: [],
  }))

  if (!activePackages.length) {
    return {primary: allRows, secondary: []}
  }

  return {
    primary: allRows.filter((row) => row.activePackages.length),
    secondary: allRows.filter((row) => !row.activePackages.length),
  }
}
